import {useEffect, useRef} from 'react';

type Event = MouseEvent | TouchEvent;

/**
 * Options:
 * triggerOnce: Trigger the provided callback only once when user clicks outside of the element
 * triggerOnceAndResetAfterClickInside: Same as triggerOnce, but also resets 'hasBeenTriggeredOnce'
 * after the user clicks inside the element again. Helps preventing repeated triggering of the handler when
 * the user clicks repeatedly outside.
 *
 * IMPORTANT: Use either 'triggerOnce' or 'triggerOnceAndResetAfterClickInside', but not both at the same time.
 * Please make sure when adding this hook that is split into seperate component as we dont want to have it triggering if not necessary
 * for example check file: /components/LanguageCountrySwitch.tsx
 */
export type OnClickOutsideOptions = {
  trigger?: 'once' | 'onceAndReset';
  disabled?: boolean;
};

export const useOnClickOutside = (
  ref: React.RefObject<HTMLElement | null>,
  handler: (event: Event) => void,
  opts: OnClickOutsideOptions = {},
) => {
  const hasBeenTriggeredOnceRef = useRef(false);
  useEffect(() => {
    const listener = (event: Event) => {
      if (opts.disabled) {
        return;
      }

      const el = ref?.current;
      // Click happened on the ref-element itself or inside it
      if (!el || el.contains((event?.target as Node) || null)) {
        if (opts.trigger === 'onceAndReset') {
          // On inner click reset 'hasBeenTriggeredOnce' so the handler
          // can be called the next time after the user clicked outside.
          hasBeenTriggeredOnceRef.current = false;
        }
        return;
      }

      // Click happened outside of the ref-element
      if (opts.trigger === 'once' || opts.trigger === 'onceAndReset') {
        if (!hasBeenTriggeredOnceRef.current) {
          hasBeenTriggeredOnceRef.current = true;
          handler(event);
        }
      } else {
        handler(event);
      }
    };

    document.addEventListener('mousedown', listener);

    return () => {
      document.removeEventListener('mousedown', listener);
    };
  }, [ref, handler, opts.trigger, hasBeenTriggeredOnceRef]);
};

export const useOnClickOutsideMultipleTargets = (
  refs: React.RefObject<HTMLElement | null>[],
  handler: (event: Event) => void,
) => {
  useEffect(() => {
    const listener = (event: Event) => {
      const el = refs.find(ref =>
        ref?.current?.contains((event?.target as Node) || null),
      );
      if (el) {
        return;
      }

      handler(event);
    };
    document.addEventListener('mousedown', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
    };
  }, [refs, handler]);
};
